import { useSyncExternalStore, useCallback } from 'react';
import { subscribeUser, getUserVersion, saveUserItem } from '../data/userStore';
import {
  FULL_APP_KEY,
  FULL_APP_PRICE_LABEL,
  FREE_ROOT_NOTES,
  hasFullApp,
  hasPatreonAccess,
  getPatreonVerifiedAt,
  hasBetaAccess,
  getBetaVerifiedAt,
  isModeUnlocked,
  isRootNoteUnlocked,
} from '../data/entitlements';
import { clearBetaEntitlement } from '../lib/beta';
import {
  subscribePurchases,
  getPurchasesVersion,
  hasRealPayments,
  getFullAppPriceLabel,
  purchaseFullApp,
  restorePurchases as restoreRealPurchases,
} from '../lib/purchases';
import {
  isPatreonEnabled,
  startPatreonLink,
  unlinkPatreon as unlinkPatreonAccount,
} from '../lib/patreon';
import { isInternalBuild } from '../config/distribution';
import { track } from '../utils/analytics';
import * as dataProvider from '../data/DataProvider';

export { FULL_APP_KEY, FULL_APP_PRICE_LABEL, FREE_ROOT_NOTES };

// Import tolérant : plusieurs suites remplacent tout le module DataProvider
// par une factory jest qui n'expose que le hook dont elles ont besoin. Sans ce
// repli, monter useEntitlement hors provider (feuille de verrou, page You) y
// casserait sur un useSyncNow absent.
const noopSyncNow = () => Promise.resolve();
const useSyncNow =
  typeof dataProvider.useSyncNow === 'function' ? dataProvider.useSyncNow : () => noopSyncNow;

// Déclenche la synchro sans attendre son résultat : l'accès est déjà posé
// localement, l'appelant rend la main tout de suite. Un échec réseau est sans
// conséquence, le prochain démarrage repoussera.
const fireAndForget = (fn) => {
  try {
    Promise.resolve(fn()).catch(() => {});
  } catch (err) {
    // La synchro est un bonus, jamais un blocage du flux d'achat.
  }
};

// Message rendu a l'UI quand aucun canal de paiement n'est disponible.
export const PAYMENTS_UNAVAILABLE =
  'Purchases are unavailable right now. Please check your connection and try again.';

// Hook React sur la couche entitlements : re-render sur tout changement du
// miroir user (achat, restore, reset, re-hydratation apres sync/login) et sur
// l'etat RevenueCat (prix localise charge, flux reel actif).
// Sur natif l'achat passe par RevenueCat ; ailleurs (web dev, desktop) le mock
// beta reste en place jusqu'a la phase web.
export function useEntitlement() {
  useSyncExternalStore(subscribeUser, getUserVersion);
  useSyncExternalStore(subscribePurchases, getPurchasesVersion);
  // Synchro déclenchable : sans elle, un achat n'atteint le serveur qu'au
  // prochain démarrage, ce qui peut durer des jours sur Android.
  const syncNow = useSyncNow();

  // `from` identifie le point d'entree (feuille de deverrouillage, dialog de
  // pack...) : sans lui, impossible de savoir quel ecran convertit.
  const unlockFullApp = useCallback(async ({ from } = {}) => {
    const channel = hasRealPayments() ? 'revenuecat' : 'mock';
    track('purchase_started', { from, channel });

    const res = await (async () => {
      if (hasRealPayments()) return purchaseFullApp();
      // Sans paiement reel, un build public n'ouvre rien : erreur recuperable,
      // aucun entitlement ecrit. Un premier lancement offline ou une config
      // RevenueCat qui echoue ne doit jamais donner l'app gratuitement.
      if (!isInternalBuild()) return { success: false, error: PAYMENTS_UNAVAILABLE };
      // Mock : simule le delai du flux d'achat natif avant de poser l'entitlement.
      await new Promise((resolve) => setTimeout(resolve, 500));
      saveUserItem('purchase', FULL_APP_KEY, {
        purchased: true,
        source: 'mock',
        at: new Date().toISOString(),
      });
      return { success: true };
    })();

    if (res.success) {
      track('purchase_completed', { from, channel });
      fireAndForget(syncNow);
    } else if (res.cancelled) track('purchase_cancelled', { from, channel });
    else track('purchase_failed', { from, channel, error: res.error });
    return res;
  }, [syncNow]);

  const restorePurchases = useCallback(async ({ from } = {}) => {
    const res = await (async () => {
      if (hasRealPayments()) return restoreRealPurchases();
      if (!isInternalBuild()) return { success: false, error: PAYMENTS_UNAVAILABLE };
      return { success: true, restored: hasFullApp() };
    })();

    if (res.success) {
      track('restore_completed', { from, restored: !!res.restored });
      // Seul un achat effectivement retrouvé change quelque chose à pousser.
      if (res.restored) fireAndForget(syncNow);
    } else track('restore_failed', { from, error: res.error });
    return res;
  }, [syncNow]);

  // Ouvre la page d'autorisation Patreon. Le claim, et donc le deverrouillage,
  // se font au retour dans l'app (usePatreonReturn).
  const linkPatreon = useCallback(async () => {
    try {
      await startPatreonLink();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const unlinkPatreon = useCallback(async () => {
    try {
      await unlinkPatreonAccount();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const disconnectBeta = useCallback(() => {
    clearBetaEntitlement();
  }, []);

  return {
    fullApp: hasFullApp(),
    isModeUnlocked,
    isRootNoteUnlocked,
    unlockFullApp,
    restorePurchases,
    priceLabel: getFullAppPriceLabel(),
    realPayments: hasRealPayments(),
    // Acces Patreon : revocable, distinct de l'achat, meme perimetre de droits.
    patreonAccess: hasPatreonAccess(),
    patreonVerifiedAt: getPatreonVerifiedAt(),
    patreonEnabled: isPatreonEnabled(),
    linkPatreon,
    unlinkPatreon,
    // Acces beta : revocable (kill switch Admin), grace offline de 7 jours.
    betaAccess: hasBetaAccess(),
    betaVerifiedAt: getBetaVerifiedAt(),
    disconnectBeta,
  };
}

export default useEntitlement;
